import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import { throttle } from 'core/libs/lodash';

import withTheme from 'core/hocs/withTheme';
import withBreakpoint from 'core/hocs/withBreakpoint';

import Image from 'core/components/Image';
import Position from 'core/components/GridPosition';
import CaptionCredits from 'core/components/CaptionCredits';

import themePropTypes from 'core/utils/prop-types/theme';

import styles from './index.styl';


class Slides extends PureComponent {
  static propTypes = {
    /**
     * Стартовая позиция разделителя в процентах.
     */
    startPosition: PropTypes.number,

    /** Даннные о виджете */
    attributes: PropTypes.object,

    /** @ignore */
    theme: themePropTypes(`{
      controls: {
        vikontImage: {
          maxHeight
        },
        vikontSlide: {
          borderRadius,
          singleCaption
        },
      }
    }`),
  };

  static defaultProps = {
    startPosition: 50,
  };

  state = {
    position: this.props.startPosition,
    width: `${(100 * 100) / (100 - this.props.startPosition)}%`,
  };

  componentDidMount() {
    window.addEventListener('resize', this.updateSize);
    window.addEventListener('mouseup', this.endMove);
    window.addEventListener('load', this.updateSize);
    this.updateSize();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateSize);
    window.removeEventListener('mouseup', this.endMove);
    window.removeEventListener('load', this.updateSize);
  }

  moving = false;
  vikontImageAtoms = this.props.theme.controls.vikontImage;

  updateSize = () => {
    if (!process.env.BROWSER_RUNTIME) return;
    throttle(() => {
      const { width, left } = this.container.getBoundingClientRect();
      const { left: imageLeft, width: imageWidth, height: imageHeight } = this.image.getBoundingClientRect();
      const {
        attributes: {
          versions: {
            original: {
              height: originalImageHeight,
              width: originalImageWidth,
            },
          },
        },
      } = this.props.attributes.image_right;

      let infoHeight = 0;
      let infoMargin = 0;

      if (this.info) {
        infoHeight = this.info.getBoundingClientRect().height;
        infoMargin = parseFloat(getComputedStyle(this.info).marginTop);
      }
      this.setState({
        width,
        left,
        imageLeft,
        imageWidth,
        imageHeight,
        infoHeight,
        infoMargin,
        originalImageHeight,
        originalImageWidth,
      });
    }, 50)();
  };

  endMove = () => {
    this.moving = false;
  };

  startMove = () => {
    this.moving = true;
  };

  moveSlider = event => {
    if (this.moving) {
      const { left, width, imageLeft, imageWidth, imageHeight, originalImageHeight, originalImageWidth } = this.state;
      const pageX = event.touches ? event.touches[0].pageX : event.pageX;
      let position = ((pageX - left) / width) * 100;

      // считаем на случай если высотка картинки больше чем maxHeight в атомах vikontImage
      // а иначе будет неправильно границы картинки считаться
      const aspectRatio = originalImageWidth / originalImageHeight;

      const currentImageHeight = Math.min(originalImageHeight, imageHeight);

      const currentImageWidth = currentImageHeight * aspectRatio;
      const currentImageLeft = imageLeft + (imageWidth - currentImageWidth) / 2;

      if (pageX <= currentImageLeft) {
        position = ((currentImageLeft - left) / width) * 100;
      } else if (pageX >= currentImageLeft + currentImageWidth) {
        position = ((currentImageLeft + currentImageWidth - left) / width) * 100;
      }

      this.setState({ position, width });
    }
  };

  renderImage = (img, isLeftImage) => {
    const {
      attributes: {
        versions: {
          original: imgSrc,
          thumbnail: imgThumbnail,
        },
        alt,
      },
    } = img;

    return (
      <Image
        src={imgSrc}
        placement={'contain'}
        width={imgSrc.width}
        maxHeight={this.vikontImageAtoms.maxHeight}
        imageRef={isLeftImage ? node => (this.image = node) : null}
        previewSrc={imgThumbnail}
        alt={alt}
      />
    );
  };

  renderImageInfo = (img, isLeft) => {
    const { singleCaption } = this.props.theme.controls.vikontSlide;

    const {
      attributes: {
        caption: imgCaption,
        credits: imgCredits,
      },
    } = img;

    const {
      left = 0,
      imageLeft = 0,
      imageWidth = 0,
    } = this.state;

    const pos = imageLeft - left;

    if (imgCaption || imgCredits) {
      return (
        <div
          ref={node => (this.info = node)}
          className={cx('info', singleCaption && '_single')}
        >
          <style jsx>{`
            .info
              position relative
              ${isLeft ? 'left' : 'right'} ${pos}px
              width ${imageWidth / 2}px
              margin-top 10px

              &._single
                position absolute
                bottom 0
          `}</style>

          <CaptionCredits caption={imgCaption} credits={imgCredits} />
        </div>
      );
    }
    return null;
  };

  render() {
    const {
      theme: {
        controls: {
          vikontSlide: {
            borderRadius,
            singleCaption,
          },
        },
      },
      attributes: {
        image_left: imageLeft,
        image_right: imageRight,
      },
    } = this.props;

    const {
      width,
      position,
      imageHeight = 0,
      infoHeight = 0,
      infoMargin = 0,
    } = this.state;

    return (
      <div
        className={styles.container}
        onMouseUp={this.endMove}
        onTouchEnd={this.endMove}
        onTouchCancel={this.endMove}
        onMouseMove={this.moveSlider}
        onTouchMove={this.moveSlider}
        onMouseDown={this.startMove}
        onTouchStart={this.startMove}
        ref={node => (this.container = node)}
        style={{ height: `${imageHeight + infoHeight + infoMargin}px` }}
      >
        <style jsx>{`
          .${styles.radiusWrapper}
            border-radius ${borderRadius}px
          .slider
            top ${imageHeight / 2}px
        `}
        </style>
        <div
          className={cx(styles.overlay, styles.overlayTop)}
          style={{ right: `${100 - position}%` }}
        >
          <div
            style={{ width }}
            className={styles.overlayContentTop}
          >
            <div className={styles.radiusWrapper}>
              <Position center>
                {this.renderImage(imageLeft, true)}
              </Position>
            </div>
            {!singleCaption && (
              <Position left>
                {this.renderImageInfo(imageLeft, true)}
              </Position>
            )}
          </div>
        </div>
        <div
          className={styles.overlay}
          style={{ left: `${position}%` }}
        >
          <div
            style={{ width }}
            className={styles.overlayContent}
          >
            <div className={styles.radiusWrapper}>
              <Position center>
                {this.renderImage(imageRight)}
              </Position>
            </div>
            {!singleCaption && (
              <Position right>
                {this.renderImageInfo(imageRight)}
              </Position>
            )}
          </div>
        </div>
        {singleCaption && this.renderImageInfo(position >= 50 ? imageLeft : imageRight, true)}
        <i
          style={{ left: `${position}%` }}
          className={cx('slider', styles.slider)}
        />
      </div>
    );
  }
}

const SlidesWithHOCs = withBreakpoint(withTheme(Slides));
SlidesWithHOCs.displayName = 'Slides';

export { Slides };
export default SlidesWithHOCs;
